import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import { getUserSearchFilterDefaults } from "src/hooks/useInitUserSearchFilter";

import {
	defaultKSK2140_R01DetailDto,
	defaultKSK2140_R01HeaderDto,
	defaultKSK2140_R01InfoDto,
	defaultKSK2140_R01InspectDto,
	defaultKSK2140_R01LotDto,
	KSK2140_R01DetailDto,
	KSK2140_R01HeaderDto,
	KSK2140_R01InfoDto,
	KSK2140_R01InspectDto,
	KSK2140_R01LotDto,
	KSK2140_R01WorkStandardDto,
} from "./Dto_KSK2140_R01";
import { getKSK2140_R01InspectKey, getSavedInspectQty } from "./Util_KSK2140_R01";

export interface KSK2140_R01Filter {
	/** ppr.comp_cd = :compCd */
	compCd: string;
	/** ppr.product_dt BETWEEN :startDate AND :endDate */
	dateRange: DateRange | undefined;
	/** ppr.workplace_cd = :workplaceCd */
	workplaceCd: string;
	/** ppr.product_emp = :orderEmp */
	orderEmp: string;
	/** 검색 UI 표시용 (API 미전송) */
	orderEmpNm: string;
}

const indexList = <T extends { valueIndex: number; orderIndex: number; orderIndexSeq: number }>(
	list: T[],
	defaults: T,
) =>
	list.map((item, index) => ({
		...defaults,
		...item,
		valueIndex: index + 1,
		orderIndex: index + 1,
		orderIndexSeq: 0,
	}));

export interface Store_KSK2140_R01 {
	KSK2140_R01Filter: KSK2140_R01Filter;
	setKSK2140_R01Filter: (filter: Partial<KSK2140_R01Filter>) => void;

	KSK2140_R01List: KSK2140_R01HeaderDto[];
	setKSK2140_R01List: (list: KSK2140_R01HeaderDto[]) => void;
	selectedKSK2140_R01List: KSK2140_R01HeaderDto[];
	setSelectedKSK2140_R01List: (data: KSK2140_R01HeaderDto[]) => void;
	initSelectedKSK2140_R01List: () => void;
	workStandardModalVisible: boolean;
	setWorkStandardModalVisible: (visible: boolean) => void;
	workStandardSheetNo: 1 | 2 | null;
	setWorkStandardSheetNo: (sheetNo: 1 | 2 | null) => void;
	KSK2140_R01WorkStandard: KSK2140_R01WorkStandardDto | undefined;
	setKSK2140_R01WorkStandard: (workStandard: KSK2140_R01WorkStandardDto | undefined) => void;

	editKSK2140_R01: KSK2140_R01InfoDto;
	setEditKSK2140_R01: (data: KSK2140_R01InfoDto) => void;

	KSK2140_R01DetailList: KSK2140_R01DetailDto[];
	setKSK2140_R01DetailList: (list: KSK2140_R01DetailDto[]) => void;
	insertKSK2140_R01DetailList: (list: KSK2140_R01DetailDto[]) => void;
	updateKSK2140_R01DetailList: (list: KSK2140_R01DetailDto[]) => void;
	removeKSK2140_R01DetailList: (valueIndexList: string[]) => void;

	selectedKSK2140_R01DetailList: KSK2140_R01DetailDto | null;
	setSelectedKSK2140_R01DetailList: (data: KSK2140_R01DetailDto | null) => void;
	initSelectedKSK2140_R01DetailList: () => void;
	inspectQtyInput: number;
	inspectQtyInputByKey: Record<string, number>;
	setInspectQtyInput: (qty: number) => void;

	deletedKSK2140_R01DetailList: string[];
	addDeletedKSK2140_R01DetailList: (valueIndexList: string[]) => void;
	removeDeletedKSK2140_R01DetailList: (valueIndexList?: string[]) => void;
	originalKSK2140_R01DetailList: KSK2140_R01DetailDto[];

	KSK2140_R01LotList: KSK2140_R01LotDto[];
	setKSK2140_R01LotList: (list: KSK2140_R01LotDto[]) => void;
	insertKSK2140_R01LotList: (list: KSK2140_R01LotDto[]) => void;
	updateKSK2140_R01LotList: (valueIndex: number, data: KSK2140_R01LotDto) => void;
	selectedKSK2140_R01LotList: string[];
	addSelectedKSK2140_R01LotList: (valueIndexList: string[]) => void;
	removeSelectedKSK2140_R01LotList: (valueIndexList: string[]) => void;
	initSelectedKSK2140_R01LotList: () => void;
	deletedKSK2140_R01LotList: string[];
	originalKSK2140_R01LotList: KSK2140_R01LotDto[];

	KSK2140_R01InspectMap: Map<string, KSK2140_R01InspectDto[]>;
	setKSK2140_R01InspectMap: (data: Map<string, KSK2140_R01InspectDto[]>) => void;
	activeInspectKey: string;
	setActiveInspectKey: (key: string) => void;
	setKSK2140_R01InspectList: (list: KSK2140_R01InspectDto[]) => void;
	insertKSK2140_R01InspectList: (list: KSK2140_R01InspectDto[]) => void;
	removeKSK2140_R01InspectList: (valueIndex: number) => void;
	updateKSK2140_R01InspectList: (valueIndex: number, data: KSK2140_R01InspectDto) => void;
	selectedKSK2140_R01InspectList: string[];
	addSelectedKSK2140_R01InspectList: (valueIndexList: string[]) => void;
	removeSelectedKSK2140_R01InspectList: (valueIndexList?: string[]) => void;
	initSelectedKSK2140_R01InspectList: () => void;
	deletedKSK2140_R01InspectList: string[];
	originalKSK2140_R01InspectList: KSK2140_R01InspectDto[];

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	lotColumnState: ColumnState[] | null;
	setLotColumnState: (state: ColumnState[] | null) => void;
	inspectColumnState: ColumnState[] | null;
	setInspectColumnState: (state: ColumnState[] | null) => void;

	initData: () => void;
}

const createDefaultKSK2140_R01Filter = (): KSK2140_R01Filter => ({
	...getUserSearchFilterDefaults(),
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	orderEmp: "",
	orderEmpNm: "",
});

export const defaultKSK2140_R01Filter = createDefaultKSK2140_R01Filter();

const getKSK2140_R01DetailKey = (row: KSK2140_R01DetailDto) =>
	`${row.basOrderNo}|${row.basLotNo || row.lotNo || ""}`;

export const useKSK2140_R01Store = create<Store_KSK2140_R01>((set) => ({
	KSK2140_R01Filter: defaultKSK2140_R01Filter,
	setKSK2140_R01Filter: (filter) =>
		set((state) => ({
			KSK2140_R01Filter: { ...state.KSK2140_R01Filter, ...filter },
		})),

	KSK2140_R01List: [],
	setKSK2140_R01List: (list) =>
		set({
			KSK2140_R01List: indexList(list, defaultKSK2140_R01HeaderDto),
		}),
	selectedKSK2140_R01List: [],
	setSelectedKSK2140_R01List: (data) => set({ selectedKSK2140_R01List: data }),
	initSelectedKSK2140_R01List: () => set({ selectedKSK2140_R01List: [] }),
	workStandardModalVisible: false,
	setWorkStandardModalVisible: (visible) => set({ workStandardModalVisible: visible }),
	workStandardSheetNo: null,
	setWorkStandardSheetNo: (sheetNo) => set({ workStandardSheetNo: sheetNo }),
	KSK2140_R01WorkStandard: undefined,
	setKSK2140_R01WorkStandard: (workStandard) => set({ KSK2140_R01WorkStandard: workStandard }),

	editKSK2140_R01: { ...defaultKSK2140_R01InfoDto },
	setEditKSK2140_R01: (data) => set({ editKSK2140_R01: data }),

	KSK2140_R01DetailList: [],
	setKSK2140_R01DetailList: (list) => {
		const detailList = indexList(list, defaultKSK2140_R01DetailDto).map(
			(item) => ({ ...item, inserted: false }),
		);
		set({
			KSK2140_R01DetailList: detailList,
			originalKSK2140_R01DetailList: detailList,
			selectedKSK2140_R01DetailList: null,
			deletedKSK2140_R01DetailList: [],
		});
	},
	insertKSK2140_R01DetailList: (list) =>
		set((state) => {
			const existingKeySet = new Set(
				state.KSK2140_R01DetailList.map(getKSK2140_R01DetailKey),
			);
			const detailList = [...state.KSK2140_R01DetailList];
			let nextValueIndex =
				Math.max(
					0,
					...state.KSK2140_R01DetailList.map((item) => item.valueIndex),
				) + 1;

			list.forEach((item) => {
				const key = getKSK2140_R01DetailKey(item);
				if (existingKeySet.has(key)) return;

				detailList.push({
					...defaultKSK2140_R01DetailDto,
					...item,
					valueIndex: nextValueIndex,
					orderIndex: nextValueIndex,
					orderIndexSeq: 0,
					inserted: true,
				});
				nextValueIndex++;
				existingKeySet.add(key);
			});

			return { KSK2140_R01DetailList: detailList };
		}),
	updateKSK2140_R01DetailList: (list) =>
		set((state) => ({
			KSK2140_R01DetailList: list,
			selectedKSK2140_R01DetailList: state.selectedKSK2140_R01DetailList
				? list.find(
					(item) => item.valueIndex === state.selectedKSK2140_R01DetailList?.valueIndex,
				) ?? null
				: null,
		})),
	removeKSK2140_R01DetailList: (valueIndexList) =>
		set((state) => ({
			KSK2140_R01DetailList: state.KSK2140_R01DetailList.filter(
				(item) =>
					!valueIndexList.some(
						(valueIndex) => Number(valueIndex) === item.valueIndex,
					),
			),
		})),

	selectedKSK2140_R01DetailList: null,
	inspectQtyInput: 0,
	inspectQtyInputByKey: {},
	setInspectQtyInput: (qty) =>
		set((state) => ({
			inspectQtyInput: qty,
			inspectQtyInputByKey: state.activeInspectKey
				? { ...state.inspectQtyInputByKey, [state.activeInspectKey]: qty }
				: state.inspectQtyInputByKey,
		})),
	setSelectedKSK2140_R01DetailList: (data) =>
		set((state) => {
			const key = data ? getKSK2140_R01InspectKey(data) : "";
			const inspectQtyInput = getSavedInspectQty(data, state.inspectQtyInputByKey);
			return {
				selectedKSK2140_R01DetailList: data,
				activeInspectKey: key,
				inspectQtyInput,
				inspectQtyInputByKey: key
					? { ...state.inspectQtyInputByKey, [key]: inspectQtyInput }
					: state.inspectQtyInputByKey,
			};
		}),
	initSelectedKSK2140_R01DetailList: () =>
		set({
			selectedKSK2140_R01DetailList: null,
			activeInspectKey: "",
			inspectQtyInput: 0,
		}),

	deletedKSK2140_R01DetailList: [],
	addDeletedKSK2140_R01DetailList: (valueIndexList) =>
		set((state) => ({
			deletedKSK2140_R01DetailList: Array.from(
				new Set([
					...state.deletedKSK2140_R01DetailList,
					...valueIndexList.filter((valueIndex) =>
						state.KSK2140_R01DetailList.some(
							(item) =>
								item.valueIndex === Number(valueIndex) && !item.inserted,
						),
					),
				]),
			),
		})),
	removeDeletedKSK2140_R01DetailList: (valueIndexList) =>
		set((state) => ({
			deletedKSK2140_R01DetailList: valueIndexList
				? state.deletedKSK2140_R01DetailList.filter(
						(valueIndex) => !valueIndexList.includes(valueIndex),
					)
				: [],
		})),
	originalKSK2140_R01DetailList: [],

	KSK2140_R01LotList: [],
	setKSK2140_R01LotList: (list) => {
		const lotList = indexList(list, defaultKSK2140_R01LotDto).map((item) => ({
			...item,
			inserted: false,
		}));
		set({
			KSK2140_R01LotList: lotList,
			originalKSK2140_R01LotList: lotList,
			selectedKSK2140_R01LotList: [],
			deletedKSK2140_R01LotList: [],
		});
	},
	insertKSK2140_R01LotList: (list) =>
		set((state) => {
			let nextValueIndex =
				Math.max(0, ...state.KSK2140_R01LotList.map((item) => item.valueIndex)) + 1;
			const lotList = [...state.KSK2140_R01LotList];
			list.forEach((item) => {
				lotList.push({
					...defaultKSK2140_R01LotDto,
					...item,
					valueIndex: nextValueIndex,
					orderIndex: nextValueIndex,
					orderIndexSeq: 0,
					inserted: true,
				});
				nextValueIndex++;
			});
			return { KSK2140_R01LotList: lotList };
		}),
	updateKSK2140_R01LotList: (valueIndex, data) =>
		set((state) => ({
			KSK2140_R01LotList: state.KSK2140_R01LotList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),
	selectedKSK2140_R01LotList: [],
	addSelectedKSK2140_R01LotList: (valueIndexList) =>
		set((state) => ({
			selectedKSK2140_R01LotList: Array.from(
				new Set([...state.selectedKSK2140_R01LotList, ...valueIndexList]),
			),
		})),
	removeSelectedKSK2140_R01LotList: (valueIndexList) =>
		set((state) => ({
			selectedKSK2140_R01LotList: state.selectedKSK2140_R01LotList.filter(
				(valueIndex) => !valueIndexList.includes(valueIndex),
			),
		})),
	initSelectedKSK2140_R01LotList: () =>
		set({ selectedKSK2140_R01LotList: [] }),
	deletedKSK2140_R01LotList: [],
	originalKSK2140_R01LotList: [],

	KSK2140_R01InspectMap: new Map<string, KSK2140_R01InspectDto[]>(),
	setKSK2140_R01InspectMap: (data) => set({ KSK2140_R01InspectMap: data }),
	activeInspectKey: "",
	setActiveInspectKey: (key) => set({ activeInspectKey: key }),
	setKSK2140_R01InspectList: (list) =>
		set((state) => {
			const { KSK2140_R01InspectMap, activeInspectKey } = state;
			if (!activeInspectKey) return state;
			const nextMap = new Map(KSK2140_R01InspectMap);
			nextMap.set(activeInspectKey, list);
			return { KSK2140_R01InspectMap: nextMap };
		}),
	insertKSK2140_R01InspectList: (list) =>
		set((state) => {
			const { KSK2140_R01InspectMap, activeInspectKey } = state;
			if (!activeInspectKey) return state;
			const nextMap = new Map(KSK2140_R01InspectMap);
			const currentList = [...(nextMap.get(activeInspectKey) || [])];
			let nextValueIndex = Math.max(0, ...currentList.map((item) => item.valueIndex)) + 1;
			list.forEach((item) => {
				currentList.push({
					...defaultKSK2140_R01InspectDto,
					...item,
					valueIndex: nextValueIndex,
					orderIndex: nextValueIndex,
					orderIndexSeq: 0,
					inserted: true,
				});
				nextValueIndex++;
			});
			nextMap.set(activeInspectKey, currentList);
			return { KSK2140_R01InspectMap: nextMap };
		}),
	removeKSK2140_R01InspectList: (valueIndex) =>
		set((state) => {
			const { KSK2140_R01InspectMap, activeInspectKey } = state;
			if (!activeInspectKey) return state;
			const nextMap = new Map(KSK2140_R01InspectMap);
			const currentList = (nextMap.get(activeInspectKey) || []).filter(
				(item) => item.valueIndex !== valueIndex,
			);
			nextMap.set(activeInspectKey, currentList);
			return {
				KSK2140_R01InspectMap: nextMap,
				selectedKSK2140_R01InspectList: state.selectedKSK2140_R01InspectList.filter(
					(id) => Number(id) !== valueIndex,
				),
				deletedKSK2140_R01InspectList: state.deletedKSK2140_R01InspectList.filter(
					(id) => Number(id) !== valueIndex,
				),
			};
		}),
	updateKSK2140_R01InspectList: (valueIndex, data) =>
		set((state) => {
			const { KSK2140_R01InspectMap, activeInspectKey } = state;
			if (!activeInspectKey) return state;
			const nextMap = new Map(KSK2140_R01InspectMap);
			const currentList = (nextMap.get(activeInspectKey) || []).map((item) =>
				item.valueIndex === valueIndex ? data : item,
			);
			nextMap.set(activeInspectKey, currentList);
			return { KSK2140_R01InspectMap: nextMap };
		}),
	selectedKSK2140_R01InspectList: [],
	addSelectedKSK2140_R01InspectList: (valueIndexList) =>
		set((state) => ({
			selectedKSK2140_R01InspectList: Array.from(
				new Set([...state.selectedKSK2140_R01InspectList, ...valueIndexList]),
			),
		})),
	removeSelectedKSK2140_R01InspectList: (valueIndexList) =>
		set((state) => ({
			selectedKSK2140_R01InspectList: valueIndexList
				? state.selectedKSK2140_R01InspectList.filter(
					(valueIndex) => !valueIndexList.includes(valueIndex),
				)
				: [],
		})),
	initSelectedKSK2140_R01InspectList: () =>
		set({ selectedKSK2140_R01InspectList: [] }),
	deletedKSK2140_R01InspectList: [],
	originalKSK2140_R01InspectList: [],

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),
	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),
	detailColumnState: null,
	setDetailColumnState: (state) => set({ detailColumnState: state }),
	detailScrollTop: null,
	setDetailScrollTop: (top) => set({ detailScrollTop: top }),
	lotColumnState: null,
	setLotColumnState: (state) => set({ lotColumnState: state }),
	inspectColumnState: null,
	setInspectColumnState: (state) => set({ inspectColumnState: state }),

	initData: () =>
		set({
			KSK2140_R01Filter: createDefaultKSK2140_R01Filter(),
			KSK2140_R01List: [],
			selectedKSK2140_R01List: [],
			workStandardModalVisible: false,
			workStandardSheetNo: null,
			KSK2140_R01WorkStandard: undefined,
			editKSK2140_R01: { ...defaultKSK2140_R01InfoDto },
			KSK2140_R01DetailList: [],
			selectedKSK2140_R01DetailList: null,
			deletedKSK2140_R01DetailList: [],
			originalKSK2140_R01DetailList: [],
			KSK2140_R01LotList: [],
			selectedKSK2140_R01LotList: [],
			deletedKSK2140_R01LotList: [],
			originalKSK2140_R01LotList: [],
			KSK2140_R01InspectMap: new Map<string, KSK2140_R01InspectDto[]>(),
			activeInspectKey: "",
			inspectQtyInput: 0,
			inspectQtyInputByKey: {},
			selectedKSK2140_R01InspectList: [],
			deletedKSK2140_R01InspectList: [],
			originalKSK2140_R01InspectList: [],
			scrollTop: null,
			detailScrollTop: null,
		}),
}));
